import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { PageMessage } from '../../../../types/PageMessage';
import type { ProductMessage } from '../../../../types/ProductMessage';
import { getLatestProducts, getVesselProducts } from '../../api/clients/gateway.api';
import { getProductsByIds, getProductsByQuery } from '../../api/clients/product.api';
import { queries } from '../../api/queryKeys/queries';
import { useRole } from '../../hooks/useRole';

import type { ProductOption } from './ProductOption';

interface UseProductSelectOptionsParams {
    defaultProducts?: ProductMessage[];
    excludedProductIds?: string[];
    value: string;
    vesselId?: string;
}

export const useProductSelectOptions = ({
    defaultProducts,
    excludedProductIds,
    value,
    vesselId,
}: UseProductSelectOptionsParams) => {
    const role = useRole();
    const queryClient = useQueryClient();
    const defaultProductIds = defaultProducts?.map((product) => product.id);

    const createProductOption = useCallback(
        (product: ProductMessage, suggested = false): ProductOption => ({
            value: product.id,
            label: product.name,
            product,
            suggested,
            inTank: !!defaultProducts?.some((defaultProduct: ProductMessage) => defaultProduct.id === product.id),
        }),
        [defaultProducts]
    );

    const loadVesselProducts = useCallback(
        async (vesselId: string) => {
            const response = await getVesselProducts(vesselId);

            const vesselProductOptions = response.products
                .filter(
                    (vesselProduct) =>
                        !excludedProductIds?.includes(vesselProduct.product.id) || vesselProduct.product.id === value
                )
                .map((vesselProduct) => ({
                    ...createProductOption(vesselProduct.product),
                    inTank:
                        vesselProduct.isInTank ||
                        !!defaultProducts?.some((defaultProduct) => defaultProduct.id === vesselProduct.product.id),
                }));

            const inTankProducts = vesselProductOptions.filter((option) => option.inTank);
            const notInTankProducts = vesselProductOptions.filter((option) => !option.inTank);

            return [...inTankProducts, ...notInTankProducts];
        },
        [createProductOption, defaultProducts, excludedProductIds, value]
    );

    const loadLatestProducts = useCallback(async () => {
        const response = await getLatestProducts();
        const productOptions = response
            .filter((product) => !excludedProductIds?.includes(product.id) || product.id === value)
            .map((product) => createProductOption(product));

        if (defaultProducts?.length) {
            const defaultProductOptions = defaultProducts
                .filter((product) => !excludedProductIds?.includes(product.id) || product.id === value)
                .map((product) => ({
                    ...createProductOption(product),
                    inTank: defaultProducts.some((defaultProduct: ProductMessage) => defaultProduct.id === product.id),
                }));

            const filteredProductOptions = productOptions.filter(
                (product) => !defaultProductOptions.some((defaultOption) => defaultOption.value === product.value)
            );
            return [...defaultProductOptions, ...filteredProductOptions];
        }

        return productOptions;
    }, [createProductOption, defaultProducts, excludedProductIds, value]);

    const loadProductsByQuery = useCallback(
        async (searchQuery: string, page: number): Promise<PageMessage<ProductOption>> => {
            const productsPage = await getProductsByQuery({
                page,
                searchQuery,
                supplierProductGroups: role.group?.supplierProductGroups,
            });

            return {
                ...productsPage,
                content: productsPage.content
                    .filter((product) => !excludedProductIds?.includes(product.id) || product.id === value)
                    .map((product) => createProductOption(product)),
            };
        },
        [createProductOption, excludedProductIds, role.group?.supplierProductGroups, value]
    );

    const loadProducts = useCallback(
        async (
            searchQuery: string,
            page: number,
            additionalFirstPageOptionCount = 0
        ): Promise<PageMessage<ProductOption>> => {
            if (!searchQuery && !role.isSupplier()) {
                if (vesselId) {
                    const vesselProductsOptions = await loadVesselProducts(vesselId);

                    if (vesselProductsOptions?.length) {
                        return {
                            content: vesselProductsOptions,
                            pageable: {
                                page: 0,
                                size: vesselProductsOptions.length + additionalFirstPageOptionCount,
                            },
                            total: vesselProductsOptions.length,
                        };
                    }
                }

                const latestProductsOptions = await loadLatestProducts();

                if (latestProductsOptions?.length) {
                    return {
                        content: latestProductsOptions,
                        pageable: {
                            page: 0,
                            size: latestProductsOptions.length + additionalFirstPageOptionCount,
                        },
                        total: latestProductsOptions.length,
                    };
                }
            }

            return loadProductsByQuery(searchQuery, page);
        },
        [loadLatestProducts, loadProductsByQuery, loadVesselProducts, role, vesselId]
    );

    const loadProductById = useCallback(
        async (productId: string): Promise<ProductOption | null> => {
            const response = await getProductsByIds([productId]);
            const product = response[0];

            return product ? createProductOption(product) : null;
        },
        [createProductOption]
    );

    const selectedValueQuery = useQuery({
        queryKey: queries.products.selectedComboBoxOption({
            defaultProductIds,
            productId: value,
        }).queryKey,
        queryFn: async () => {
            const option = await loadProductById(value);

            return option ? [option] : [];
        },
        enabled: !!value,
        staleTime: Infinity,
    });

    const cacheSelectedValue = (option: ProductOption) => {
        queryClient.setQueryData(
            queries.products.selectedComboBoxOption({
                defaultProductIds,
                productId: option.value,
            }).queryKey,
            [option]
        );
    };

    return {
        createProductOption,
        cacheSelectedValue,
        loadProductById,
        loadProducts,
        selectedValue: value ? selectedValueQuery.data?.[0] ?? null : null,
    };
};
